import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, Line, ComposedChart } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

const HistogramGraph = ({ data, soundmeterId, weighting, colorScheme = 'default' }) => {
  if (!data || !data.data || data.data.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-slate-500">
          Aucune donnée disponible pour l'histogramme
        </CardContent>
      </Card>
    );
  }

  const colorSchemes = {
    default: '#0d9488',
    ocean: '#06b6d4',
    forest: '#10b981',
    sunset: '#f97316'
  };

  const baseColor = colorSchemes[colorScheme] || colorSchemes.default;

  // Calculate cumulative distribution
  let cumulative = 0;
  const chartData = data.data.map((bin, index) => {
    cumulative += bin.count;
    return {
      range: bin.range,
      count: bin.count,
      cumulative: cumulative,
      percentage: ((bin.count / data.data.reduce((sum, b) => sum + b.count, 0)) * 100).toFixed(1)
    };
  });

  // Calculate statistics
  const totalCount = data.data.reduce((sum, bin) => sum + bin.count, 0);
  const maxCount = Math.max(...data.data.map(bin => bin.count));
  const modalClass = data.data.reduce((prev, current) => 
    (prev.count > current.count) ? prev : current
  );

  // Generate color gradient based on count
  const getColor = (count, maxCount) => {
    const intensity = count / maxCount;
    const r = parseInt('0d', 16);
    const g = parseInt('94', 16);
    const b = parseInt('88', 16);
    
    return `rgba(${r}, ${g}, ${b}, ${0.3 + intensity * 0.7})`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Histogramme de Distribution - {weighting}</span>
          <div className="flex space-x-2">
            <Badge variant="outline" className="bg-teal-50">
              Total: {totalCount} mesures
            </Badge>
            <Badge variant="outline" className="bg-purple-50">
              Classe modale: {modalClass.range}
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Main Histogram */}
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="range" 
              label={{ value: 'Niveau sonore (dB)', position: 'insideBottom', offset: -10 }}
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              yAxisId="left"
              label={{ value: 'Fréquence (occurrences)', angle: -90, position: 'insideLeft' }}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              label={{ value: 'Cumul (%)', angle: 90, position: 'insideRight' }}
            />
            <Tooltip 
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px', border: '1px solid #e2e8f0' }}
              formatter={(value, name) => {
                if (name === 'Fréquence') return [`${value} occurrences`, name];
                if (name === 'Cumul') return [`${value} occurrences`, name];
                if (name === 'Pourcentage') return [`${value}%`, name];
                return [value, name];
              }}
            />
            <Legend wrapperStyle={{ paddingTop: '20px' }} />
            <Bar 
              yAxisId="left"
              dataKey="count" 
              name="Fréquence"
              fill={baseColor}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getColor(entry.count, maxCount)} />
              ))}
            </Bar>
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="cumulative" 
              stroke="#8b5cf6" 
              strokeWidth={2}
              dot={{ r: 4 }}
              name="Cumul"
            />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Statistical Summary */}
        <div className="mt-6 p-4 bg-slate-50 rounded-lg">
          <h4 className="font-medium mb-3">Statistiques de Distribution</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-3 rounded text-center">
              <div className="text-slate-500 text-sm">Classe Modale</div>
              <div className="font-bold text-xl mt-1 text-teal-600">{modalClass.range}</div>
              <div className="text-xs text-slate-400 mt-1">
                {modalClass.count} occurrences ({((modalClass.count / totalCount) * 100).toFixed(1)}%)
              </div>
            </div>
            <div className="bg-white p-3 rounded text-center">
              <div className="text-slate-500 text-sm">Étendue</div>
              <div className="font-bold text-xl mt-1 text-blue-600">
                {data.data[0].range} - {data.data[data.data.length - 1].range}
              </div>
              <div className="text-xs text-slate-400 mt-1">Plage de mesure</div>
            </div>
            <div className="bg-white p-3 rounded text-center">
              <div className="text-slate-500 text-sm">Classes</div>
              <div className="font-bold text-xl mt-1 text-purple-600">{data.data.length}</div>
              <div className="text-xs text-slate-400 mt-1">Intervalles</div>
            </div>
            <div className="bg-white p-3 rounded text-center">
              <div className="text-slate-500 text-sm">Mesures Totales</div>
              <div className="font-bold text-xl mt-1 text-emerald-600">{totalCount}</div>
              <div className="text-xs text-slate-400 mt-1">Échantillons</div>
            </div>
          </div>
        </div>

        {/* Percentage Distribution Table */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium mb-3">Distribution en Pourcentage</h4>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-sm">
            {chartData.map((bin, index) => (
              <div key={index} className="bg-white p-2 rounded text-center border">
                <div className="font-medium text-xs text-slate-600">{bin.range}</div>
                <div className="font-bold text-sm mt-1">{bin.percentage}%</div>
              </div>
            ))}
          </div>
        </div>

        {/* Quartile Analysis */}
        <div className="mt-4 p-4 bg-green-50 rounded-lg">
          <h4 className="font-medium mb-3">Analyse par Quartiles</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <div className="bg-white p-3 rounded">
              <div className="text-slate-500">Q1 (25%)</div>
              <div className="font-bold text-lg mt-1">
                {chartData.find(d => d.cumulative >= totalCount * 0.25)?.range || 'N/A'}
              </div>
            </div>
            <div className="bg-white p-3 rounded">
              <div className="text-slate-500">Q2 (50% - Médiane)</div>
              <div className="font-bold text-lg mt-1">
                {chartData.find(d => d.cumulative >= totalCount * 0.50)?.range || 'N/A'}
              </div>
            </div>
            <div className="bg-white p-3 rounded">
              <div className="text-slate-500">Q3 (75%)</div>
              <div className="font-bold text-lg mt-1">
                {chartData.find(d => d.cumulative >= totalCount * 0.75)?.range || 'N/A'}
              </div>
            </div>
            <div className="bg-white p-3 rounded">
              <div className="text-slate-500">Q4 (95%)</div>
              <div className="font-bold text-lg mt-1">
                {chartData.find(d => d.cumulative >= totalCount * 0.95)?.range || 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Info Summary */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Sonomètre:</span>
            <div className="font-semibold">{soundmeterId}</div>
          </div>
          <div>
            <span className="text-slate-500">Pondération:</span>
            <div className="font-semibold">{weighting}</div>
          </div>
          <div>
            <span className="text-slate-500">Période:</span>
            <div className="font-semibold">24h</div>
          </div>
          <div>
            <span className="text-slate-500">Type:</span>
            <div className="font-semibold">Distribution + Cumul</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default HistogramGraph;
